import { Button, CharField, Flex, H1, useBreadcrumbs } from "@inmagik/magik-ui"
import Layout from "../../components/Layout"
import { useNavigate, useParams } from "react-router-dom"
import AtlanteForm from "./AtlanteForm"
import { useMemo, useState } from "react"
import { TableTavole } from "../Tavole/TableTavole"
import { AtlanteFormType, Edizione, FiltersBase } from "../../types"
import { useQsFilters } from "../../hooks/filters"
import { Container } from "react-bootstrap"
import useModalTrigger from "magik-react-hooks/useModalTrigger"
import { Formik } from "formik"
import BottomFormFixed from "../../components/BottomFormFixed"

import { initialValuesAtlante, validationSchemaAtlante } from "./AtlanteCommon"

import ModalDelete from "../../components/ModalDelete"
import { ScrollLayout } from "../../components/ScrollLayout"
import { useAuth } from "../../auth"
import { transformErrorForForm } from "../../utils/utils"
import { useAtlante, useAtlanteDelete, useAtlanteUpdate } from "../../hooks/atlanti"
import { useTavolaCreate, useTavolaUpdate, useTavoleList } from "../../hooks/tavole"
import TavolaModal from "../Tavole/TavolaModal"

const initFilters = (params: URLSearchParams) =>
  ({
    page: Number(params.get("page") ?? 1),
    search: params.get("search") ?? "",
    ordering: params.get("ordering") ?? "",
  } as FiltersBase)

export default function AtlanteUpdate() {
  const { filters, uiFilters, setFilters } = useQsFilters(initFilters)
  const { id, ...wildcard } = useParams()
  const { data: atlante } = useAtlante(Number(id))

  const updateAtlante = useAtlanteUpdate()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState(wildcard["*"] || "dettaglio")

  const { data: tavole } = useTavoleList({
    page: String(filters.page),
    search: filters.search,
    ordering: filters.ordering,
  })

  const createTavola = useTavolaCreate()
  const updateTavola = useTavolaUpdate()

  const [modalTavolaCreateState, modalTavolaCreateActions] = useModalTrigger()
  const [modalTavolaUpdateState, modalTavolaUpdateActions] = useModalTrigger()

  const deleteAtlante = useAtlanteDelete()
  const [modalDeleteAtlanteState, modalDeleteAtlanteActions] = useModalTrigger()

  const initialValues = useMemo(() => initialValuesAtlante(atlante), [atlante])
  const validationSchema = useMemo(() => validationSchemaAtlante(), [])

  useBreadcrumbs([{ label: "Atlanti", href: "/atlanti/atlanti" }, { label: "Dettaglio atlante" }])

  const { user } = useAuth()

  return (
    <Layout>
      <ScrollLayout hasBottomFixed>
        <Flex alignItems="center" justifyContent="between">
          <H1>{atlante?.nome || <>&nbsp;</>}</H1>
          {activeTab === "dettaglio" && user?.can_delete_data && (
            <Button
              size="md"
              title="Elimina"
              variant="danger"
              leadingIcon="delete"
              onClick={() => {
                modalDeleteAtlanteActions.open()
              }}
            />
          )}
          {activeTab === "tavole" && (
            <Button
              size="md"
              className="text-white"
              title="Aggiungi"
              leadingIcon="add"
              onClick={() => {
                modalTavolaCreateActions.open()
              }}
            />
          )}
        </Flex>
        <ScrollLayout.Tabs
          className="mt-6"
          onTabChange={(tab) => {
            setActiveTab(tab)
          }}
        >
          <ScrollLayout.Tab id="dettaglio" label="Dettaglio" key={"dettaglio"}>
            <Formik
              enableReinitialize
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={(values, actions) => {
                const payload: Partial<AtlanteFormType> = { ...values }
                if (typeof payload.immagine_copertina === "string") {
                  delete payload.immagine_copertina
                }
                return updateAtlante
                  .mutateAsync(payload as AtlanteFormType)
                  .then((atlante) => {
                    navigate(`/atlanti/atlanti/${atlante.id}`)
                  })
                  .catch((e) => {
                    actions.setErrors(transformErrorForForm(e))
                  })
              }}
            >
              {({ handleSubmit, dirty, isSubmitting }) => (
                <form onSubmit={handleSubmit}>
                  <Container fluid className="mt-6">
                    <AtlanteForm />
                    <BottomFormFixed url="/atlanti/atlanti" />
                  </Container>
                </form>
              )}
            </Formik>
          </ScrollLayout.Tab>
          <ScrollLayout.Tab id="tavole" label="Tavole" key={"tavole"}>
            <Flex className="mt-3">
              <CharField
                label=""
                placeholder="Cerca..."
                leadingIcon="search"
                style={{
                  width: 300,
                }}
                value={filters.search}
                onChange={(value) => {
                  setFilters({ ...filters, search: value })
                }}
              />
            </Flex>
            <div className="mt-3">
              <TableTavole
                tavole={tavole?.results || []}
                setFilters={setFilters}
                filters={uiFilters}
                count={tavole?.count || 0}
                fullCount={tavole?.full_count || 0}
                actionLink={modalTavolaUpdateActions.open}
              />
            </div>
          </ScrollLayout.Tab>
        </ScrollLayout.Tabs>
      </ScrollLayout>
      {modalTavolaUpdateState.isOpen && (
        <TavolaModal
          show={modalTavolaUpdateState.isOpen}
          onClose={modalTavolaUpdateActions.close}
          tavola={modalTavolaUpdateState.value}
          save={(values) => {
            return updateTavola
              .mutateAsync({
                ...values,
                atlante: Number(id),
              })
              .then(() => {
                modalTavolaUpdateActions.close()
              })
          }}
          title="Modifica tavola"
        />
      )}
      {modalTavolaCreateState.isOpen && (
        <TavolaModal
          show={modalTavolaCreateState.isOpen}
          onClose={modalTavolaCreateActions.close}
          save={(values) => {
            return createTavola
              .mutateAsync({
                ...values,
                atlante: Number(id),
              })
              .then(() => {
                modalTavolaCreateActions.close()
              })
          }}
          title="Aggiungi tavola"
        />
      )}

      {modalDeleteAtlanteState.isOpen && (
        <ModalDelete
          isOpen={modalDeleteAtlanteState.isOpen}
          onClose={modalDeleteAtlanteActions.close}
          onDelete={() => {
            return deleteAtlante.mutateAsync(Number(id)).then(() => {
              navigate("/atlanti/atlanti")
            })
          }}
          message="Sei sicuro di voler eliminare l'atlante?"
          title="Elimina atlante"
        />
      )}
    </Layout>
  )
}
